/**
 * useClippr.tsx - React hook for Clippr
 * 
 * CHANGE HISTORY:
 * - 251102_1210 [pending] danielbeer: Created React hooks for Clippr rebuild
 * 
 * React hook for accessing Clippr clipboard manager with automatic updates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ClipprManager } from '../core/ClipprManager';
import type {
  ClipboardItem,
  ClipboardOptions,
  PasteOptions,
  ClipboardEvent,
  ClipboardStats,
  FileMakerClipboardItem
} from '../types';

export interface UseClipprOptions {
  clippr?: ClipprManager;
  autoSubscribe?: boolean;
}

export interface UseClipprReturn {
  // State
  hasData: boolean;
  clipboard: ClipboardItem | null;
  history: ClipboardItem[];
  slots: string[];
  stats: ClipboardStats;
  
  // Basic operations
  copy: (data: any, options?: ClipboardOptions) => Promise<ClipboardItem>;
  cut: (data: any, options?: ClipboardOptions) => Promise<ClipboardItem>;
  paste: (options?: PasteOptions) => any;
  peek: (slot?: string) => any;
  clear: (slot?: string) => void;
  
  // History operations
  pasteFromHistory: (index: number) => any;
  
  // Slot operations
  getSlots: () => string[];
  
  // FileMaker operations
  copyFileMakerXML: (xml: string, options?: ClipboardOptions) => Promise<ClipboardItem>;
  pasteFileMakerXML: (options?: PasteOptions) => FileMakerClipboardItem | null;
  hasFileMakerXML: (slot?: string) => boolean;
  getFileMakerMetadata: (slot?: string) => FileMakerClipboardItem | null;
  
  // Utility
  refresh: () => void;
}

/**
 * React hook for Clippr clipboard manager
 */
export function useClippr(options: UseClipprOptions = {}): UseClipprReturn {
  const { clippr, autoSubscribe = true } = options;
  
  // Get or create Clippr instance
  const clipprRef = useRef<ClipprManager>(clippr || new ClipprManager());
  const manager = clipprRef.current;
  
  // State
  const [hasData, setHasData] = useState(manager.hasData());
  const [clipboard, setClipboard] = useState<ClipboardItem | null>(manager.peek());
  const [history, setHistory] = useState<ClipboardItem[]>(manager.getHistory());
  const [slots, setSlots] = useState<string[]>(manager.getSlots());
  const [stats, setStats] = useState<ClipboardStats>(manager.getStats());
  
  // Refresh all state
  const refresh = useCallback(() => {
    setHasData(manager.hasData());
    setClipboard(manager.peek());
    setHistory(manager.getHistory());
    setSlots(manager.getSlots());
    setStats(manager.getStats());
  }, [manager]);
  
  // Subscribe to clipboard events
  useEffect(() => {
    if (!autoSubscribe) return;
    
    const handleEvent = (event: ClipboardEvent) => {
      // Update relevant state based on event type
      switch (event.type) {
        case 'copy':
        case 'cut':
        case 'paste':
        case 'clear':
          refresh();
          break;
        case 'history-add':
          setHistory(manager.getHistory());
          break;
        case 'slot-change':
          setSlots(manager.getSlots());
          break;
      }
    };
    
    const unsubscribe = manager.subscribe(handleEvent);
    
    return () => {
      unsubscribe();
    };
  }, [manager, autoSubscribe, refresh]);
  
  // Basic operations
  const copy = useCallback(async (data: any, opts?: ClipboardOptions) => {
    const item = await manager.copy(data, opts);
    refresh();
    return item;
  }, [manager, refresh]);
  
  const cut = useCallback(async (data: any, opts?: ClipboardOptions) => {
    const item = await manager.cut(data, opts);
    refresh();
    return item;
  }, [manager, refresh]);
  
  const paste = useCallback((opts?: PasteOptions) => {
    const data = manager.paste(opts);
    refresh();
    return data;
  }, [manager, refresh]);
  
  const peek = useCallback((slot?: string) => {
    return manager.peek(slot);
  }, [manager]);
  
  const clear = useCallback((slot?: string) => {
    manager.clear(slot);
    refresh();
  }, [manager, refresh]);
  
  // History operations
  const pasteFromHistory = useCallback((index: number) => {
    const data = manager.pasteFromHistory(index);
    refresh();
    return data;
  }, [manager, refresh]);
  
  // Slot operations
  const getSlots = useCallback(() => {
    return manager.getSlots();
  }, [manager]);
  
  // FileMaker operations
  const copyFileMakerXML = useCallback(async (xml: string, opts?: ClipboardOptions) => {
    const item = await manager.copyFileMakerXML(xml, opts);
    refresh();
    return item;
  }, [manager, refresh]);
  
  const pasteFileMakerXML = useCallback((opts?: PasteOptions) => {
    const data = manager.pasteFileMakerXML(opts);
    refresh();
    return data;
  }, [manager, refresh]);
  
  const hasFileMakerXML = useCallback((slot?: string) => {
    return manager.hasFileMakerXML(slot);
  }, [manager]);
  
  const getFileMakerMetadata = useCallback((slot?: string) => {
    return manager.getFileMakerMetadata(slot);
  }, [manager]);
  
  return {
    // State
    hasData,
    clipboard,
    history,
    slots,
    stats,
    
    // Basic operations
    copy,
    cut,
    paste,
    peek,
    clear,
    
    // History operations
    pasteFromHistory,
    
    // Slot operations
    getSlots,
    
    // FileMaker operations
    copyFileMakerXML,
    pasteFileMakerXML,
    hasFileMakerXML,
    getFileMakerMetadata,
    
    // Utility
    refresh
  };
}
